import { writable } from "svelte/store";
import { getLocalStorage, setLocalStorage } from "$lib/utils/localStorage";

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresIn?: number;
  refreshTokenExpiresIn?: number;
}

const INITIAL_STATE: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
};

function createAuthStore() {
  // Initialize the store with values from localStorage/cookies if available
  const accessToken = getLocalStorage('demo_token') || null;
  const refreshToken = getLocalStorage('refresh_token') || null;
  
  // If we have tokens in storage, consider the user authenticated
  const initialState: AuthState = {
    isAuthenticated: !!accessToken,
    accessToken,
    refreshToken,
  };

  const { subscribe, set, update } = writable<AuthState>(initialState);

  // Sync with cookies when running in browser
  function syncWithCookies() {
    if (typeof document === 'undefined') return;
    
    const cookieAccessToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('demo_token='))
      ?.split('=')[1];
      
    const cookieRefreshToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('refresh_token='))
      ?.split('=')[1];
    
    if (cookieAccessToken && !getLocalStorage('demo_token')) {
      setLocalStorage('demo_token', cookieAccessToken);
    }
    
    if (cookieRefreshToken && !getLocalStorage('refresh_token')) {
      setLocalStorage('refresh_token', cookieRefreshToken);
    }
    
    update(state => ({
      ...state,
      accessToken: cookieAccessToken || state.accessToken,
      refreshToken: cookieRefreshToken || state.refreshToken,
      isAuthenticated: !!(cookieAccessToken || state.accessToken),
    }));
  }

  // Set authentication data
  function setAuth(authData: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresIn?: number;
    refreshTokenExpiresIn?: number;
  }) {
    // Update localStorage
    setLocalStorage('demo_token', authData.accessToken);
    setLocalStorage('refresh_token', authData.refreshToken);
    
    // Update cookies if in browser
    if (typeof document !== 'undefined') {
      const accessExpiry = authData.accessTokenExpiresIn ? `; max-age=${authData.accessTokenExpiresIn}` : '';
      const refreshExpiry = authData.refreshTokenExpiresIn ? `; max-age=${authData.refreshTokenExpiresIn}` : '';
      
      document.cookie = `demo_token=${authData.accessToken}; path=/${accessExpiry}`;
      document.cookie = `refresh_token=${authData.refreshToken}; path=/${refreshExpiry}`;
    }
    
    // Update store
    update(state => ({
      ...state,
      isAuthenticated: true,
      accessToken: authData.accessToken,
      refreshToken: authData.refreshToken,
      accessTokenExpiresIn: authData.accessTokenExpiresIn,
      refreshTokenExpiresIn: authData.refreshTokenExpiresIn,
    }));
  }

  // Clear authentication data
  function clearAuth() {
    // Clear localStorage
    setLocalStorage('demo_token', '');
    setLocalStorage('refresh_token', '');
    
    // Clear cookies if in browser
    if (typeof document !== 'undefined') {
      document.cookie = 'demo_token=; path=/; max-age=0';
      document.cookie = 'refresh_token=; path=/; max-age=0';
    }
    
    // Reset store to initial state
    set(INITIAL_STATE);
  }

  // Get current access token
  function getAccessToken(): string | null {
    syncWithCookies(); // Ensure we have the latest token
    let state: AuthState;
    subscribe(s => { state = s; })();
    return state.accessToken;
  }

  // Get current refresh token
  function getRefreshToken(): string | null {
    syncWithCookies(); // Ensure we have the latest token
    let state: AuthState;
    subscribe(s => { state = s; })();
    return state.refreshToken;
  }

  // Check if user is authenticated
  function isAuthenticated(): boolean {
    syncWithCookies(); // Ensure we have the latest token
    let state: AuthState;
    subscribe(s => { state = s; })();
    return state.isAuthenticated;
  }

  // Initialize the store by checking for tokens
  function initialize() {
    syncWithCookies();
  }

  return {
    subscribe,
    setAuth,
    clearAuth,
    getAccessToken,
    getRefreshToken,
    isAuthenticated,
    initialize,
  };
}

export const authStore = createAuthStore();

// Initialize the store when this module is imported
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    authStore.initialize();
  });
}